import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { API } from "../../../constants/api";
import { errorMessage } from "../../../utils/errorMessage";
import useAuthAxios from "../../../utils/baseAxios";
import { formatAmount, formatDateWithSuffix } from "../../../utils/formatTime";
import { MdRefresh } from "react-icons/md";
import { useInView } from "react-intersection-observer";
import ClipLoader from "react-spinners/ClipLoader";
import { darkNoTransaction, noTransaction } from "../../../assets/images";
import { useUser } from "../../../context/user-context";
import EarningDetailsModal from "./EarningDetailsModal";

const Earnings = () => {
  const axiosInstance = useAuthAxios();
  const { theme } = useUser();
  const [earnings, setEarnings] = useState<any[]>([]);
  const [selectedEarning, setSelectedEarning] = useState<any>(null);

  const [page, setPage] = useState<number>(1);
  const divRef = useRef<HTMLDivElement | null>(null);
  const [ref, inView] = useInView();
  const getPoint = async () => {
    const response = await axiosInstance.get(API.getPointsActivity);
    return response.data;
  };
  const {
    data: pointActivity,
    error: error1,
    isLoading,
  } = useQuery({
    queryKey: ["get-point-activity", page],
    queryFn: getPoint,
    retry: 1,
  });
  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);
  const earningsList = pointActivity?.data?.claimRequests;
  const totalPages = pointActivity?.data.meta.totalPages || 0;

  useEffect(() => {
    if (inView) {
      if (page < totalPages) {
        setPage((prev) => prev + 1);
      }
    }
  }, [inView, totalPages]);
  useEffect(() => {
    if (earningsList) {
      const newEarnings = [...earnings, ...earningsList];

      // Remove duplicates by notification ID
      const uniqueEarnings = Array.from(
        new Map(newEarnings.map((earn) => [earn._id, earn])).values()
      );

      // Newest first
      uniqueEarnings.sort(
        (a, b) =>
          new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
          new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
      );

      setEarnings(uniqueEarnings);
    }
  }, [earningsList]);
  //   console.log(earnings);

  const handleEarningClick = (earning: any) => {
    setSelectedEarning(earning);
  };

  const handleCloseModal = () => {
    setSelectedEarning(null);
  };

  return (
    <>
      <div className="bg-transparent rounded-xl  mt-4 h-[450px] overflow-auto">
        <div ref={divRef} className="w-full h-[450px]">
          {earnings.length >= 1 ? (
            <>
              <div className="w-full flex flex-col gap-3">
                {earnings.map((earn: any, index: any) => {
                  const statusPillClass =
                    earn?.status === "PENDING"
                      ? "text-[#CB9200] bg-[#CB9200]/20"
                      : earn?.status === "APPROVED"
                      ? "text-[#03C668] bg-[#03C668]/20"
                      : earn?.status === "DECLINED"
                      ? "text-red-700 bg-red-700/20"
                      : "text-gray-500 bg-gray-500/20";
                  return (
                    <div
                      key={index}
                      className="flex justify-between gap-3 items-start cursor-pointer rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2227] p-4 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      onClick={() => handleEarningClick(earn)}
                    >
                      <div className="flex justify-start items-center gap-3 min-w-0">
                        <div className="flex justify-center items-center w-10 h-10 shrink-0 rounded-full bg-gray-200 dark:bg-gray-600">
                          <MdRefresh className="text-gray-700 dark:text-gray-200 text-[15px]" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-gray-900 dark:text-gray-50 font-semibold text-[14px]">
                            Redeemed {earn?.points} points
                          </h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            <span className="text-gray-500 dark:text-gray-400 text-[12px]">
                              {formatDateWithSuffix(earn.updatedAt)}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${statusPillClass}`}
                            >
                              {earn?.status ?? "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <h4 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px] shrink-0">
                        ₦ {formatAmount(earn?.cashAmount)}
                      </h4>
                    </div>
                  );
                })}
                <div ref={ref} className=""></div>
              </div>
            </>
          ) : (
            !isLoading && (
              <div className="flex flex-col justify-center mt-16 lgss:mt-24 items-center">
                {theme === "dark" ? (
                  <img src={darkNoTransaction} alt="" />
                ) : (
                  <img src={noTransaction} alt="" />
                )}
                <h4 className="font-bold text-[#869AA9] pt-2">No earning</h4>
                <p className="text-[#869AA9] pt-2 text-[12px]">
                  All your earnings will show up here
                </p>
              </div>
            )
          )}
          {isLoading && (
            <div className="mt-6 flex justify-center items-center">
              <ClipLoader color="#0B75C2" size={60} />
            </div>
          )}
        </div>
      </div>

      {selectedEarning && (
        <EarningDetailsModal
          earning={selectedEarning}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default Earnings;
